'use client';

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Avatar, AvatarImage } from './ui/avatar';
import Button from './Button';

interface ContactTestimonial {
	quote: string;
	name: string;
	title: string;
	company: string;
	avatar?: string;
}

interface ContactCTAProps {
	title?: string;
	description?: React.ReactNode;
	buttons?: {
		text: string;
		link: string;
		color?: 'dark' | 'primary' | 'neutral';
	}[];
	testimonials?: ContactTestimonial[];
}

const AUTO_SLIDE_INTERVAL = 6000;
const FADE_DURATION = 300;

/** Full-bleed from inside a `.container` parent without shifting content inset */
const CTA_SECTION_CLASS =
	'bg-primary-600 text-white relative w-screen max-w-[100vw] left-1/2 -ml-[50vw] overflow-hidden';

/**
 * Viewport-aligned gutters (same math as theme `container`: 1rem + centered max-width).
 * Avoids nested `.container` inside markdown/pages, which doubles horizontal padding.
 */
const CTA_CONTENT_CLASS =
	'relative z-10 w-full px-4 md:px-6 lg:pl-[calc(max(0px,(100vw-1280px)/2)+1rem)] xl:pl-[calc(max(0px,(100vw-1440px)/2)+1rem)] lg:pr-[calc(max(0px,(100vw-1280px)/2)+1rem)] xl:pr-[calc(max(0px,(100vw-1440px)/2)+1rem)]';

/**
 * A `description` passed as rich text arrives wrapped in `Markdown`, whose own
 * `[&_p]:text-lg` sets a line-height at equal specificity but later in the sheet.
 * `!` is what keeps the CTA's line-height from losing that tie.
 */
const CTA_DESCRIPTION_CLASS =
	'text-white/90 [&_p]:text-base md:[&_p]:text-lg [&_p]:!leading-[1.5]';

function CtaPatternBackground() {
	return (
		<div
			className="absolute inset-0 pointer-events-none bg-[url(/cta-bg-pattern.svg)] opacity-50 bg-repeat-x bg-[length:auto_100%] bg-center"
			aria-hidden
		/>
	);
}

const DEFAULT_TITLE = (
	<>
		Ready to talk logistics?
		<br />
		So are we.
	</>
);

const DEFAULT_DESCRIPTION = (
	<p>
		From warehousing and distribution to value-added services such as kitting and assembly, our
		team of experts is dedicated to providing the solutions you need to succeed.
	</p>
);

export default function ContactCTA({
	title,
	description,
	buttons,
	testimonials = [],
}: ContactCTAProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [displayedIndex, setDisplayedIndex] = useState(0);
	const [visible, setVisible] = useState(true);
	const hasTestimonials = testimonials.length > 0;

	const goTo = useCallback((index: number) => {
		setVisible(false);
		setTimeout(() => {
			setDisplayedIndex(index);
			setCurrentIndex(index);
			setVisible(true);
		}, FADE_DURATION);
	}, []);

	const goNext = useCallback(() => {
		if (!hasTestimonials) return;
		goTo((currentIndex + 1) % testimonials.length);
	}, [currentIndex, goTo, hasTestimonials, testimonials.length]);

	const goPrev = useCallback(() => {
		if (!hasTestimonials) return;
		goTo((currentIndex - 1 + testimonials.length) % testimonials.length);
	}, [currentIndex, goTo, hasTestimonials, testimonials.length]);

	useEffect(() => {
		if (!hasTestimonials) return;
		const timer = setInterval(goNext, AUTO_SLIDE_INTERVAL);
		return () => clearInterval(timer);
	}, [goNext, hasTestimonials]);

	const testimonial = hasTestimonials ? testimonials[displayedIndex % testimonials.length] : null;

	/** Default copy is a matched pair: a caller supplying its own title also owns its description. */
	const resolvedDescription = description ?? (title ? null : DEFAULT_DESCRIPTION);

	if (!hasTestimonials) {
		return (
			<section className={CTA_SECTION_CLASS}>
				<CtaPatternBackground />
				<div className={`${CTA_CONTENT_CLASS} py-16 md:py-32`}>
					<div className="flex flex-col gap-6 max-w-2xl">
						<h2 className="text-[32px] lg:text-[64px] font-bold leading-[1.33]">
							{title || DEFAULT_TITLE}
						</h2>
						{resolvedDescription && (
							<div className={CTA_DESCRIPTION_CLASS}>
								{resolvedDescription}
							</div>
						)}
						<div className="flex flex-wrap gap-4">
							{buttons && buttons.length > 0 ? (
								buttons.map((btn) => (
									<Button
										key={`${btn.link}-${btn.text}`}
										href={btn.link}
										color={btn.color || 'neutral'}
									>
										{btn.text}
									</Button>
								))
							) : (
								<Button href="/contact" color="neutral">
									Contact Us
								</Button>
							)}
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className={CTA_SECTION_CLASS}>
			<CtaPatternBackground />
			<div
				className={`${CTA_CONTENT_CLASS} py-16 md:py-32 flex flex-col md:flex-row md:items-stretch gap-10 md:gap-16`}
			>
				{/* Left: Heading + CTA */}
				<div className="flex flex-col gap-6 md:w-1/2">
						<h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.33]">
							{title || DEFAULT_TITLE}
						</h2>
						{resolvedDescription && (
							<div className={CTA_DESCRIPTION_CLASS}>
								{resolvedDescription}
							</div>
						)}
						<div className="flex flex-wrap gap-4">
							{buttons && buttons.length > 0 ? (
								buttons.map((btn) => (
									<Button
										key={`${btn.link}-${btn.text}`}
										href={btn.link}
										color={btn.color || 'neutral'}
									>
										{btn.text}
									</Button>
								))
							) : (
								<Button href="/contact" color="neutral">
									Contact Us
								</Button>
							)}
						</div>
					</div>

				{/* Right: Testimonial Slider */}
				<div className="md:w-1/2">
						{hasTestimonials && testimonial ? (
							<div className="flex flex-col gap-6">
								<div
									className={`transition-opacity duration-300 ease-in-out ${visible ? 'opacity-100' : 'opacity-0'}`}
								>
									<Avatar className="w-10 h-10 mb-4">
										<AvatarImage
											className="grayscale"
											alt={testimonial.name}
											src={testimonial.avatar}
										/>
									</Avatar>
									<p className="text-base md:text-lg font-medium leading-relaxed">
										{testimonial.quote}
									</p>
									<div className="mt-4">
										<p className="text-sm text-white/80">
											{testimonial.name}, {testimonial.title}
										</p>
										<p className="text-sm text-white/80">
											{testimonial.company}
										</p>
									</div>
								</div>

								{/* Navigation Arrows */}
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={goPrev}
										aria-label="Previous testimonial"
										className="w-9 h-9 flex items-center justify-center rounded border border-white/30 hover:bg-white/10 transition-colors"
									>
										<ChevronLeft className="w-4 h-4" />
									</button>
									<button
										type="button"
										onClick={goNext}
										aria-label="Next testimonial"
										className="w-9 h-9 flex items-center justify-center rounded border border-white/30 hover:bg-white/10 transition-colors"
									>
										<ChevronRight className="w-4 h-4" />
									</button>
								</div>
							</div>
						) : null}
				</div>
			</div>
		</section>
	);
}
